import { createUseStyles } from 'react-jss';

import { ThemeProps } from '@styles/theme';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
    },

    topBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },

    topBarActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },

    refreshMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },

    refreshMetaLabel: {
        fontSize: 12,
        fontWeight: 700,
        color: theme.colors.grey_text,
        letterSpacing: '0.02em',
    },

    legacyButton: {
        minHeight: 38,
        padding: `0 ${theme.spacing(2)}`,
    },

    groupField: {
        minWidth: 240,
        background: theme.colors.white,
        borderRadius: theme.radius,
    },

    hero: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: theme.radius,
        padding: theme.spacing(5),
        background: theme.isDark
            ? `linear-gradient(140deg, #08061a 0%, #1a1240 42%, #061428 100%)`
            : `linear-gradient(140deg, ${theme.colors.deep_blue} 0%, ${theme.colors.violet_primary} 42%, ${theme.colors.blue_primary} 100%)`,
        boxShadow: theme.isDark
            ? '0 26px 60px rgba(0, 0, 0, 0.45)'
            : '0 26px 60px rgba(23, 19, 56, 0.18)',
        color: '#ffffff',
        [theme.down('desktop')]: {
            padding: theme.spacing(4),
        },
    },

    heroGlow: {
        position: 'absolute',
        inset: 'auto -10% -45% auto',
        width: 280,
        height: 280,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 72%)',
        pointerEvents: 'none',
    },

    heroHeader: {
        position: 'relative',
        zIndex: 1,
        marginBottom: theme.spacing(3),
    },

    heroEyebrow: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        opacity: 0.88,
        marginBottom: theme.spacing(1.5),
    },

    heroTitle: {
        fontFamily: theme.font.primary,
        fontSize: 34,
        lineHeight: 1.05,
        fontWeight: 700,
        [theme.down('desktop')]: {
            fontSize: 28,
        },
    },

    heroMetaGrid: {
        position: 'relative',
        zIndex: 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: theme.spacing(2),
        [theme.down('desktop')]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [theme.down('tablet')]: {
            gridTemplateColumns: '1fr',
        },
    },

    heroMetaCard: {
        background: 'rgba(255, 255, 255, 0.12)',
        border: '1px solid rgba(255, 255, 255, 0.16)',
        borderRadius: theme.radius,
        padding: theme.spacing(3),
        backdropFilter: 'blur(14px)',
    },

    heroMetaLabel: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        opacity: 0.8,
        marginBottom: theme.spacing(1),
    },

    heroMetaValue: {
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.1,
        marginBottom: theme.spacing(0.75),
        color: '#ffffff',
    },

    heroMetaHint: {
        fontSize: 13,
        lineHeight: 1.5,
        opacity: 0.82,
    },

    heroMetaAction: {
        border: 'none',
        background: 'none',
        padding: 0,
        textAlign: 'left',
        cursor: 'pointer',
        color: 'inherit',
    },

    controlsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        alignItems: 'center',
        justifyContent: 'space-between',
        [theme.down('desktop')]: {
            alignItems: 'stretch',
            flexDirection: 'column',
        },
    },

    rangeControls: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        alignItems: 'center',
    },

    rangeButton: {
        border: 'none',
        background: theme.colors.white,
        color: theme.colors.grey_text,
        fontFamily: theme.font.secondary,
        fontSize: 13,
        fontWeight: 700,
        borderRadius: 999,
        padding: `${theme.spacing(1.8)} ${theme.spacing(2.8)}`,
        boxShadow: '0 8px 24px rgba(39, 31, 77, 0.08)',
        cursor: 'pointer',
        transition: 'transform 160ms ease, box-shadow 160ms ease, background 160ms ease, color 160ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 12px 28px rgba(39, 31, 77, 0.12)',
        },
        '&[data-active="true"]': {
            background: theme.colors.violet_primary,
            color: theme.colors.white,
        },
    },

    refreshButton: {
        minWidth: 110,
    },

    loadingIntro: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
    },

    loadingEyebrow: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: theme.colors.violet_primary,
    },

    loadingTitle: {
        fontFamily: theme.font.primary,
        fontSize: 36,
        lineHeight: 1.05,
        fontWeight: 700,
        color: theme.colors.black,
        [theme.down('desktop')]: {
            fontSize: 28,
        },
    },

    loadingBars: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
        height: 44,
    },

    loadingBarTall: {
        width: 12,
        height: 40,
        borderRadius: 999,
        background: theme.colors.violet_primary,
        boxShadow: '0 10px 18px rgba(91, 54, 188, 0.24)',
    },

    loadingBarMedium: {
        width: 12,
        height: 28,
        borderRadius: 999,
        background: theme.colors.blue_primary,
        boxShadow: '0 10px 18px rgba(61, 92, 240, 0.22)',
    },

    loadingBarShort: {
        width: 12,
        height: 18,
        borderRadius: 999,
        background: theme.colors.blue_neon,
        boxShadow: '0 10px 18px rgba(68, 203, 255, 0.22)',
    },

    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
        gap: theme.spacing(2),
        [theme.down('desktop')]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [theme.down('tablet')]: {
            gridTemplateColumns: '1fr',
        },
    },

    summaryCard: {
        background: theme.colors.surface_elevated,
        borderRadius: theme.radius,
        padding: theme.spacing(3),
        border: `1px solid ${theme.colors.grey_light}`,
        boxShadow: theme.isDark
            ? '0 4px 16px rgba(0,0,0,0.32)'
            : '0 16px 32px rgba(39, 31, 77, 0.06)',
    },

    summaryLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.colors.grey_text,
        marginBottom: theme.spacing(1.2),
    },

    summaryValue: {
        fontSize: 28,
        fontWeight: 700,
        lineHeight: 1.1,
        color: theme.colors.black,
        fontFamily: theme.font.primary,
        marginBottom: theme.spacing(1),
    },

    summaryHint: {
        fontSize: 13,
        lineHeight: 1.5,
        color: theme.colors.grey_text,
    },

    ongoingStrip: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: theme.spacing(2),
    },

    ongoingCard: {
        border: `1px solid ${theme.colors.grey_light}`,
        borderRadius: theme.radius,
        background: `linear-gradient(180deg, ${theme.colors.white} 0%, ${theme.colors.surface_elevated} 100%)`,
        padding: theme.spacing(2.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
        boxShadow: '0 10px 24px rgba(39, 31, 77, 0.05)',

        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.isDark ? '0 16px 28px rgba(0, 0, 0, 0.45)' : '0 16px 28px rgba(39, 31, 77, 0.1)',
            borderColor: theme.colors.blue_primary,
            background: theme.isDark
                ? `linear-gradient(180deg, rgba(61, 130, 246, 0.1) 0%, rgba(61, 130, 246, 0.05) 100%)`
                : undefined,
        },
    },

    ongoingCardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        minWidth: 0,
    },

    ongoingTitle: {
        fontSize: 15,
        lineHeight: 1.35,
        fontWeight: 700,
        color: theme.colors.black,
        minWidth: 0,
    },

    ongoingBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        color: theme.colors.indigo_primary,
        background: 'rgba(99, 102, 241, 0.14)',
        whiteSpace: 'nowrap',
    },

    ongoingMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        fontSize: 12,
        lineHeight: 1.5,
        color: theme.colors.grey_text,
    },

    dashboardGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.2fr) minmax(320px, 1fr)',
        gap: theme.spacing(3),
        [theme.down('desktop')]: {
            gridTemplateColumns: '1fr',
        },
    },

    panel: {
        background: theme.colors.white,
        borderRadius: theme.radius,
        border: `1px solid ${theme.colors.grey_light}`,
        boxShadow: '0 16px 34px rgba(39, 31, 77, 0.06)',
        overflow: 'hidden',
    },

    panelHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(3),
        borderBottom: `1px solid ${theme.colors.grey_light}`,
    },

    panelTitle: {
        fontSize: 18,
        fontWeight: 700,
        color: theme.colors.black,
        fontFamily: theme.font.primary,
    },

    panelSubtitle: {
        fontSize: 13,
        lineHeight: 1.5,
        color: theme.colors.grey_text,
        marginTop: theme.spacing(0.6),
    },

    panelBody: {
        padding: theme.spacing(3),
    },

    healthList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },

    healthRow: {
        display: 'grid',
        gridTemplateColumns: '120px minmax(0, 1fr) 56px',
        gap: theme.spacing(2),
        alignItems: 'center',
    },

    healthLabel: {
        fontSize: 14,
        color: theme.colors.black,
        fontWeight: 600,
    },

    track: {
        position: 'relative',
        height: 12,
        borderRadius: 999,
        background: theme.colors.grey_lighter,
        overflow: 'hidden',
    },

    fill: {
        height: '100%',
        borderRadius: 999,
    },

    healthValue: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.colors.grey_text,
        textAlign: 'right',
    },

    activityBars: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(22px, 1fr))',
        gap: theme.spacing(1.2),
        alignItems: 'end',
        minHeight: 180,
    },

    activityBarColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
    },

    activityBarValue: {
        width: '100%',
        borderRadius: '12px 12px 4px 4px',
        minHeight: 12,
        background: `linear-gradient(180deg, ${theme.colors.blue_neon} 0%, ${theme.colors.violet_primary} 100%)`,
        boxShadow: '0 10px 18px rgba(61, 92, 240, 0.18)',
    },

    activityBarStacked: {
        width: '100%',
        borderRadius: '6px 6px 4px 4px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column-reverse',
    },

    activityBarSegment: {
        width: '100%',
        minHeight: 2,
        transition: 'height 200ms ease',
    },

    pulseLegend: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: `${theme.spacing(0.8)} ${theme.spacing(2)}`,
        marginTop: theme.spacing(2),
    },

    pulseLegendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.8),
        fontSize: 11,
        color: theme.colors.grey_text,
        fontWeight: 600,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        transition: 'opacity 180ms ease',
        '&:hover': { opacity: '0.75 !important' },
    },

    pulseLegendCtrl: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginLeft: 'auto',
        flexShrink: 0,
    },

    pulseLegendCtrlBtn: {
        background: 'none',
        border: `1px solid ${theme.colors.grey_light}`,
        borderRadius: 4,
        padding: '2px 7px',
        fontSize: 10,
        fontWeight: 700,
        color: theme.colors.grey_text,
        cursor: 'pointer',
        lineHeight: 1.5,
        letterSpacing: '0.03em',
        transition: 'all 120ms ease',
        '&:hover': {
            borderColor: theme.colors.blue_primary,
            color: theme.colors.blue_primary,
        },
    },

    pulseLegendDot: {
        width: 10,
        height: 10,
        borderRadius: 999,
        flexShrink: 0,
    },

    pulseToggle: {
        flexShrink: 0,
        border: `1.5px solid ${theme.colors.grey_light}`,
        borderRadius: 999,
        background: theme.colors.white,
        color: theme.colors.grey_text,
        fontFamily: theme.font.secondary,
        fontSize: 11,
        fontWeight: 700,
        padding: `3px 10px`,
        cursor: 'pointer',
        letterSpacing: '0.04em',
        transition: 'background 140ms ease, color 140ms ease, border-color 140ms ease',
        whiteSpace: 'nowrap',
        '&[data-active="true"]': {
            background: theme.colors.violet_primary,
            color: theme.colors.white,
            borderColor: theme.colors.violet_primary,
        },
        '&:not([data-active="true"]):hover': {
            borderColor: theme.colors.violet_primary,
            color: theme.colors.violet_primary,
        },
    },

    activityBarCount: {
        fontSize: 11,
        fontWeight: 700,
        color: theme.colors.black,
        lineHeight: 1,
    },

    activityBarLabel: {
        fontSize: 11,
        color: theme.colors.grey_text,
    },

    chipPopoverBackdrop: {
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
    },

    chipPopoverBox: {
        position: 'absolute',
        zIndex: 1301,
        background: theme.colors.white,
        borderRadius: 12,
        boxShadow: theme.isDark
            ? '0 8px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.07)'
            : '0 8px 32px rgba(39,31,77,0.15), 0 0 0 1px rgba(39,31,77,0.07)',
        maxHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },

    pulseBarArrowLeft: {
        position: 'absolute',
        left: -7,
        width: 7,
        height: 14,
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            width: 14,
            height: 14,
            background: theme.colors.white,
            boxShadow: theme.isDark ? '-2px -2px 4px rgba(0,0,0,0.4)' : '-2px -2px 4px rgba(39,31,77,0.1)',
            transform: 'rotate(45deg)',
            transformOrigin: 'center center',
        },
    },

    pulseBarArrowRight: {
        position: 'absolute',
        right: -7,
        width: 7,
        height: 14,
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            width: 14,
            height: 14,
            background: theme.colors.white,
            boxShadow: theme.isDark ? '2px -2px 4px rgba(0,0,0,0.4)' : '2px -2px 4px rgba(39,31,77,0.1)',
            transform: 'rotate(45deg)',
            transformOrigin: 'center center',
        },
    },

    pulseBarPopupAccent: {
        height: 4,
        borderRadius: '12px 12px 0 0',
        background: `linear-gradient(90deg, ${theme.colors.violet_primary} 0%, ${theme.colors.blue_neon} 100%)`,
        flexShrink: 0,
    },

    pulseBarPopupHeaderContent: {
        padding: `${theme.spacing(1.5)} ${theme.spacing(2)} ${theme.spacing(1)}`,
        borderBottom: `1px solid ${theme.colors.grey_light}`,
        flexShrink: 0,
    },

    pulseBarPopupHeaderTop: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },

    pulseBarPopupDate: {
        fontSize: 14,
        fontWeight: 800,
        color: theme.colors.black,
        fontFamily: theme.font.primary,
        lineHeight: 1.3,
    },

    pulseBarPopupCount: {
        fontSize: 11,
        color: theme.colors.grey_text,
        marginTop: 2,
    },

    pulseBarPopupSummaryRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
        marginTop: theme.spacing(0.75),
    },

    pulseBarPopupSummaryPill: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        fontSize: 11,
        color: theme.colors.grey_text,
        fontWeight: 600,
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        transition: 'opacity 180ms ease',
    },

    pulseBarPopupSummaryDot: {
        width: 7,
        height: 7,
        borderRadius: 999,
        flexShrink: 0,
    },

    chipModalClose: {
        flexShrink: 0,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: theme.colors.grey_text,
        fontSize: 14,
        lineHeight: 1,
        padding: 2,
        borderRadius: 4,
        '&:hover': { color: theme.colors.black },
    },

    pulseBarPopupScroller: {
        overflowY: 'auto',
        flex: 1,
        padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: theme.colors.grey_light, borderRadius: 2 },
    },

    pulseBarPopupList: {
        display: 'flex',
        flexDirection: 'column',
    },

    pulseBarPopupRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
        borderBottom: `1px solid ${theme.colors.grey_light}`,
        '&:last-child': { borderBottom: 'none' },
        cursor: 'pointer',
        borderRadius: 8,
        transition: 'background 120ms ease',
        '&:hover': {
            background: theme.isDark ? 'rgba(108,52,239,0.13)' : 'rgba(108,52,239,0.07)',
        },
        '&:active': {
            background: theme.isDark ? 'rgba(108,52,239,0.22)' : 'rgba(108,52,239,0.14)',
        },
    },

    pulseBarPopupAvatar: {
        flexShrink: 0,
        width: 34,
        height: 34,
        borderRadius: 9,
        background: `linear-gradient(135deg, ${theme.colors.violet_primary} 0%, ${theme.colors.blue_neon} 100%)`,
        color: theme.colors.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.02em',
    },

    pulseBarPopupRowMain: {
        flex: 1,
        overflow: 'hidden',
    },

    pulseBarPopupName: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.colors.black,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },

    pulseBarPopupRowSub: {
        fontSize: 10,
        color: theme.colors.grey_text,
        marginTop: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },

    pulseBarPopupBadges: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 4,
        flexShrink: 0,
    },

    pulseBarPopupBadge: {
        borderRadius: 999,
        padding: '2px 7px',
        fontSize: 10,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        letterSpacing: '0.02em',
    },

    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },

    listRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 90px',
        gap: theme.spacing(2),
        alignItems: 'center',
    },

    listRowInteractive: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 90px',
        gap: theme.spacing(2),
        alignItems: 'center',
        borderRadius: theme.radius,
        padding: `${theme.spacing(1.5)} ${theme.spacing(1.5)}`,
        cursor: 'pointer',
        transition: 'transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease',

        '&:hover': {
            transform: 'translateY(-1px)',
            borderColor: theme.colors.violet_primary,
            boxShadow: theme.isDark ? '0 4px 14px rgba(0, 0, 0, 0.4)' : '0 4px 8px rgba(39, 31, 77, 0.12)',
            backgroundColor: theme.isDark ? 'rgba(99, 102, 241, 0.14)' : theme.colors.surface_elevated,
        },
    },

    listMain: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },

    listTitle: {
        fontSize: 14,
        fontWeight: 700,
        color: theme.colors.black,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },

    listMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        fontSize: 12,
        color: theme.colors.grey_text,
    },

    listCount: {
        fontSize: 24,
        fontWeight: 700,
        textAlign: 'right',
        color: theme.colors.black,
        fontFamily: theme.font.primary,
    },

    linkButton: {
        border: 'none',
        background: 'none',
        padding: 0,
        textAlign: 'left',
        cursor: 'pointer',
        color: theme.colors.violet_primary,
        '&:hover $listTitle, &:hover $issueName, &:hover $recentTitle': {
            color: theme.colors.violet_primary,
        },
    },

    roleBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: `2px 8px`,
        borderRadius: 999,
        background: 'rgba(59, 130, 246, 0.12)',
        color: theme.colors.blue_primary,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.03em',
    },

    procedureBarTrack: {
        marginTop: theme.spacing(1),
        height: 8,
        borderRadius: 999,
        background: theme.colors.grey_lighter,
        overflow: 'hidden',
    },

    procedureBarFill: {
        height: '100%',
        borderRadius: 999,
        background: `linear-gradient(90deg, ${theme.colors.violet_primary} 0%, ${theme.colors.blue_primary} 100%)`,
    },

    issueGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: theme.spacing(3),
        [theme.down('desktop')]: {
            gridTemplateColumns: '1fr',
        },
    },

    issueColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        borderRadius: theme.radius,
        border: `1px solid ${theme.colors.grey_light}`,
        background: theme.colors.surface_elevated,
    },

    issueColumnHeader: {
        paddingBottom: theme.spacing(1.5),
        borderBottom: `1px solid ${theme.colors.grey_light}`,
    },

    issueTitle: {
        fontSize: 14,
        fontWeight: 700,
        color: theme.colors.black,
        marginBottom: theme.spacing(1.5),
    },

    issueList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },

    issueRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
    },

    issueName: {
        fontSize: 13,
        color: theme.colors.black,
        fontWeight: 600,
        minWidth: 0,
    },

    issueValue: {
        fontSize: 12,
        color: theme.colors.grey_text,
        fontWeight: 700,
        textAlign: 'right',
        whiteSpace: 'nowrap',
    },

    supportText: {
        fontSize: 14,
        lineHeight: 1.6,
        color: theme.colors.grey_text,
    },

    chipRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
    },

    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1.2),
        padding: `4px 12px`,
        borderRadius: 999,
        background: theme.colors.transp_violet,
        color: theme.colors.violet_primary,
        fontSize: 12,
        fontWeight: 700,
    },

    chipValue: {
        color: theme.colors.black,
    },

    recentGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(2),
        [theme.down('tablet')]: {
            gridTemplateColumns: '1fr',
        },
    },

    recentCard: {
        borderRadius: theme.radius,
        border: `1px solid ${theme.colors.grey_light}`,
        padding: theme.spacing(3),
        background: `linear-gradient(180deg, ${theme.colors.white} 0%, ${theme.colors.surface_elevated} 100%)`,
    },

    recentCardInteractive: {
        borderRadius: theme.radius,
        border: `1px solid ${theme.colors.grey_light}`,
        padding: theme.spacing(3),
        background: `linear-gradient(180deg, ${theme.colors.white} 0%, ${theme.colors.surface_elevated} 100%)`,
        cursor: 'pointer',
        transition: 'transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease',

        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.isDark ? '0 8px 20px rgba(0, 0, 0, 0.45)' : '0 8px 18px rgba(39, 31, 77, 0.1)',
            background: theme.isDark
                ? 'linear-gradient(180deg, rgba(99, 102, 241, 0.14) 0%, rgba(99, 102, 241, 0.08) 100%)'
                : undefined,
            backgroundColor: theme.isDark ? undefined : theme.colors.surface_elevated,
            borderColor: theme.isDark ? theme.colors.violet_primary : undefined,
        },
    },

    recentTitle: {
        display: 'inline-block',
        fontSize: 15,
        fontWeight: 700,
        color: theme.colors.black,
        marginBottom: theme.spacing(1),
    },

    recentMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.75),
        fontSize: 12,
        color: theme.colors.grey_text,
    },

    recentMetaChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.75),
    },

    recentMetaChip: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: `4px 10px`,
        borderRadius: 999,
        background: theme.colors.surface_elevated,
        color: theme.colors.grey_text,
        fontSize: 12,
        lineHeight: 1.4,
    },

    statusPill: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: `2px 8px`,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
    },

    emptyState: {
        padding: theme.spacing(6),
        borderRadius: theme.radius,
        background: theme.colors.white,
        border: `1px solid ${theme.colors.grey_light}`,
        textAlign: 'center',
        boxShadow: '0 16px 34px rgba(39, 31, 77, 0.06)',
    },

    emptyTitle: {
        fontSize: 20,
        fontWeight: 700,
        color: theme.colors.black,
        marginBottom: theme.spacing(1),
    },

    emptyDescription: {
        fontSize: 14,
        lineHeight: 1.6,
        color: theme.colors.grey_text,
        maxWidth: 520,
        margin: '0 auto',
    },

    loaderWrap: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 320,
    },

    loader: {
        color: theme.colors.violet_primary,
    },

    errorCard: {
        padding: theme.spacing(4),
        borderRadius: theme.radius,
        background: theme.colors.error_bg,
        border: `1px solid ${theme.colors.red_transparent}`,
        color: theme.colors.deep_red,
    },
}));